'use client';

import { useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import styles from './tooltip.module.css';

/**
 * Tooltip - the house hover/focus explainer. Replaces the native `title=`
 * wherever the hover text carries real meaning (provenance chips, gate states,
 * authored-field meanings); `title` stays only as an incidental fallback
 * elsewhere. Behaviour over chrome:
 *
 * - Opens after a short delay on hover, and instantly on keyboard focus
 *   (`:focus-visible`); while open the anchor carries `aria-describedby`.
 * - **Warm hover:** for a short window after any tooltip closes, the next one
 *   opens with no delay, so sweeping the pointer along a row of chips reads as
 *   one continuous explainer instead of a wait per chip. A module-level
 *   last-closed timestamp is the entire mechanism - no provider.
 * - Positions centered over the anchor (flipping to the other side when the
 *   viewport is short), clamped to the viewport edges, and rendered in a body
 *   portal so overflow containers never clip it.
 * - Dismisses on pointer-down (you acted), blur, Escape, scroll, and resize.
 * - Touch never opens it: a tooltip is supplementary here, never the only
 *   carrier of the information.
 *
 * `content` takes a plain string or rich nodes; falsy content renders the
 * children untouched. The wrapper span is `display: contents`, so it adds no
 * box: the children keep their own layout (flex rows included) and the anchor
 * rect is measured off the wrapped content itself.
 */

const OPEN_DELAY_MS = 350;
/** How long after one tooltip closes its siblings still open instantly. */
const WARM_WINDOW_MS = 300;
/** Gap between anchor and tooltip, and the minimum inset from viewport edges. */
const GAP_PX = 7;
const EDGE_PX = 8;

let lastClosedAt = 0;

export function Tooltip({
  content,
  children,
  placement = 'top',
}: {
  /** Plain string or rich nodes. Falsy renders the children untouched. */
  content: ReactNode;
  children: ReactNode;
  /** Preferred side; the opposite side is used when the viewport says no. */
  placement?: 'top' | 'bottom';
}) {
  const id = useId();
  const wrapRef = useRef<HTMLSpanElement>(null);
  const tipRef = useRef<HTMLDivElement>(null);
  const timer = useRef<number | null>(null);
  const openRef = useRef(false);
  const [open, setOpen] = useState(false);

  const clearTimer = () => {
    if (timer.current !== null) {
      window.clearTimeout(timer.current);
      timer.current = null;
    }
  };
  const reveal = () => {
    openRef.current = true;
    setOpen(true);
  };
  const show = (delay: number) => {
    clearTimer();
    if (delay <= 0) reveal();
    else timer.current = window.setTimeout(reveal, delay);
  };
  const hide = () => {
    clearTimer();
    if (openRef.current) lastClosedAt = Date.now();
    openRef.current = false;
    setOpen(false);
  };

  // Position before paint: centered on the wrapped content's rect (a Range
  // covers all children of the display:contents span), flipped when the
  // preferred side would leave the viewport, clamped at the edges.
  useLayoutEffect(() => {
    if (!open) return;
    const wrap = wrapRef.current;
    const tip = tipRef.current;
    if (!wrap || !tip) return;
    const range = document.createRange();
    range.selectNodeContents(wrap);
    const r = range.getBoundingClientRect();
    if (r.width === 0 && r.height === 0) return;
    const x = Math.min(
      Math.max(r.left + r.width / 2 - tip.offsetWidth / 2, EDGE_PX),
      window.innerWidth - tip.offsetWidth - EDGE_PX,
    );
    const above = r.top - tip.offsetHeight - GAP_PX;
    const below = r.bottom + GAP_PX;
    const place =
      placement === 'top'
        ? above >= EDGE_PX
          ? 'top'
          : 'bottom'
        : below + tip.offsetHeight <= window.innerHeight - EDGE_PX
          ? 'bottom'
          : 'top';
    tip.style.left = `${x}px`;
    tip.style.top = `${place === 'top' ? above : below}px`;
    tip.dataset.place = place;
  }, [open, placement, content]);

  // The anchor describes itself by the tooltip while it is open (a11y). When
  // the tooltip was opened by focus, the focused element is the anchor; on
  // hover, fall back to the first wrapped element.
  useEffect(() => {
    if (!open) return;
    const wrap = wrapRef.current;
    if (!wrap) return;
    const active = document.activeElement;
    const anchor =
      active instanceof HTMLElement && wrap.contains(active) ? active : wrap.firstElementChild;
    if (!(anchor instanceof HTMLElement)) return;
    const prev = anchor.getAttribute('aria-describedby');
    anchor.setAttribute('aria-describedby', prev ? `${prev} ${id}` : id);
    return () => {
      if (prev) anchor.setAttribute('aria-describedby', prev);
      else anchor.removeAttribute('aria-describedby');
    };
  }, [open, id]);

  // While open: Escape, scroll, or resize dismisses (the position is stale).
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') hide();
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('scroll', hide, true);
    window.addEventListener('resize', hide);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('scroll', hide, true);
      window.removeEventListener('resize', hide);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Never leave a pending open-timer behind on unmount.
  useEffect(() => clearTimer, []);

  if (content === null || content === undefined || content === '' || content === false) {
    return <>{children}</>;
  }

  return (
    <>
      <span
        ref={wrapRef}
        className={styles.anchor}
        onPointerEnter={(e) => {
          if (e.pointerType === 'touch') return;
          show(Date.now() - lastClosedAt < WARM_WINDOW_MS ? 0 : OPEN_DELAY_MS);
        }}
        onPointerLeave={hide}
        onPointerDown={hide}
        onFocus={(e) => {
          if (e.target.matches(':focus-visible')) show(0);
        }}
        onBlur={hide}
      >
        {children}
      </span>
      {open &&
        createPortal(
          <div ref={tipRef} id={id} role="tooltip" className={styles.tip}>
            {content}
          </div>,
          document.body,
        )}
    </>
  );
}
